import React, { useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";

import { uploadFile } from "../../services/api/uploadFile";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { StepOne } from "./StepOne";
import { StepTwo } from "./StepTwo";
import { StepThree } from "./StepThree";
import image_1 from "../assets/image_1.png";
import image_2 from "../assets/image_2.png";
import image_3 from "../assets/image_3.png";

// Types & APIs
import { User } from "../../types/user";
import { APIResponseType } from "../../types/generics/APIResponse.type";
import { useCreateTalentProfile } from "../../services/api/talent.api";
import { registerTalentValidationSchema } from "../../pages/Register/RegisterTalent.util copy";
import { useAuth } from "../../hooks";

// Gradient button for brand styling
export const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #8E2DE2 0%, #4A00E0 100%)",
  color: "var(--neutral-100)",
  font: "var(--semibold-md)",
  textTransform: "none",
  whiteSpace: "nowrap",
  borderRadius: "var(--spacing-3x)",
  padding: theme.spacing(1.4, 3),
  "&:hover": {
    background: "linear-gradient(90deg, #7329b3 0%, #3f00b2 100%)",
    boxShadow: "0 4px 14px rgba(138, 36, 235, 0.3)",
  },
}));

/* ------------------------------------------------------------------
   MAIN Multi-Step Container
-------------------------------------------------------------------- */
export const RegisterTalentForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [step, setStep] = useState(1);
  const [skill, setSkill] = useState("");
  const { isLoading, update } = useCreateTalentProfile({
    key: ["talent-profile"],
  });
  interface TalentFormValues {
    username: string;
    categories: string;
    bio: string;
    profile_image: string | File;
    cover_image: string | File;
    portfolio_links: string[];
    skills: string[];
    contact_preferences: "Email";
    social_links: [];
    coffe_call_enabled: true;
    coffe_call_price: 10;
    coffe_call_duration: 30;
    coffe_call_availability: "Scheduled";
  }
  const { errors, handleSubmit, handleChange, setFieldValue, values, touched } =
    useFormik<TalentFormValues>({
      initialValues: {
        username: "",
        categories: "",
        bio: "",
        profile_image: "",
        cover_image: "",
        portfolio_links: [""],
        skills: [],
        contact_preferences: "Email",
        social_links: [],
        coffe_call_enabled: true,
        coffe_call_price: 10,
        coffe_call_duration: 30,
        coffe_call_availability: "Scheduled",
      },
      validateOnBlur: false,
      validationSchema: registerTalentValidationSchema,
      onSubmit: async (formValues) => {
        try {
          let profileImageUrl = "";
          let coverImageUrl = "";

          // Handle profile image upload
          const isProfileFile =
            typeof formValues.profile_image !== "string" &&
            formValues.profile_image instanceof File;

          if (isProfileFile) {
            profileImageUrl = await uploadFile(
              formValues.profile_image as File
            );
          } else if (
            typeof formValues.profile_image === "string" &&
            formValues.profile_image.trim() !== ""
          ) {
            profileImageUrl = formValues.profile_image;
          }

          // Handle cover image upload
          const isCoverFile =
            typeof formValues.cover_image !== "string" &&
            formValues.cover_image instanceof File;

          if (isCoverFile) {
            coverImageUrl = await uploadFile(formValues.cover_image as File);
          } else if (
            typeof formValues.cover_image === "string" &&
            formValues.cover_image.trim() !== ""
          ) {
            coverImageUrl = formValues.cover_image;
          }

          if (!profileImageUrl) {
            enqueueSnackbar(
              "Please upload a valid profile image before submitting.",
              {
                variant: "warning",
              }
            );
            return;
          }

          // Construct final payload
          const payload = {
            ...formValues,
            profile_image: profileImageUrl,
            cover_image: coverImageUrl || "",
            categories: Array.isArray(formValues.categories)
              ? formValues.categories
              : [formValues.categories],
            portfolio_links: formValues.portfolio_links.filter(
              (link) => link.trim() !== ""
            ),
            social_links: formValues.social_links.filter(
              (link: string) => link?.trim() !== ""
            ),
            coffe_call_enabled: formValues.coffe_call_enabled,
            coffe_call_price: Number(formValues.coffe_call_price) || 0,
            coffe_call_duration: Number(formValues.coffe_call_duration) || 0,
            coffe_call_availability:
              formValues.coffe_call_availability || "Scheduled",
          };
          
          // Final API call
          await update(payload, {
            onSuccess: (data: APIResponseType<User>) => {
              setUser(data?.data);
              
              // Check if there's a stored redirect path from the withOutAuthentication HOC
              const redirectPath = sessionStorage.getItem('redirectAfterProfile');
              if (redirectPath) {
                sessionStorage.removeItem('redirectAfterProfile');
                navigate(redirectPath);
              } else {
                navigate(`/user/${data.data.id}`);
              }
            },
            onError: (err) => {
              const errorMsg =
                (err as any)?.response?.data?.message ||
                "Failed to register talent.";
              enqueueSnackbar(errorMsg, { variant: "error" });
            },
          });
        } catch (error: any) {
          console.log(error?.message || "Failed to register talent.", "error");
        }
      },
    });

  const { setUser } = useAuth();

  const stepLabel = `${step} of 3`;
  const handleNext = () => {
    if (step === 1){
      if (!values.username || !values.categories ) {
        enqueueSnackbar("Please fill out required fields before proceeding.", {
          variant: "warning",
        });
        return;
      }
      if (errors?.portfolio_links) {
        enqueueSnackbar(
          errors.portfolio_links[0] || "Please enter a valid URL",
          {
            variant: "warning",
          }
        );
        return;
      }
      if (errors?.social_links) {
        enqueueSnackbar(
          errors.social_links[0] || "Please enter a valid URL",
          {
            variant: "warning",
          }
        );
        return;
      }
    }
    setStep((prev) => prev + 1);
  };
  const handleBack = () => setStep((prev) => prev - 1);
  const getRightImageSrc = () => {
    switch (step) {
      case 1:
        return image_1;
      case 2:
        return image_2;
      default:
        return image_3;
    }
  };

  return (
    <Box sx={{ width: "100%", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "flex-start",
              justifyContent: "center",
              gap: { xs: 4, md: 8, lg: 12 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h4"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  letterSpacing: 1,
                  textAlign: isSmallScreen ? "center" : "left",
                }}
              >
                StageLink
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  mb: 3,
                  justifyContent: isSmallScreen ? "center" : "flex-start",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {stepLabel}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {[1, 2, 3].map((s) => (
                    <Box
                      key={s}
                      sx={{
                        width: 50,
                        height: 8,
                        borderRadius: 2,
                        backgroundColor: s <= step ? "#8E2DE2" : "#eee",
                      }}
                    />
                  ))}
                </Box>
              </Stack>
              {step === 1 && (
                <StepOne
                  touched={touched}
                  errors={errors}
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  handleNext={handleNext}
                />
              )}
              {step === 2 && (
                <StepTwo
                  values={values}
                  setFieldValue={setFieldValue}
                  handleNext={handleNext}
                  handleBack={handleBack}
                />
              )}
              {step === 3 && (
                <StepThree
                  handleBack={handleBack}
                  isLoading={isLoading}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              )}
            </Box>
            {!isSmallScreen && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={getRightImageSrc()}
                  alt="Talent illustration"
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 5,
                    objectFit: "cover",
                    maxHeight: 650,
                  }}
                />
              </Box>
            )}
          </Box>
        </form>
      </Container>
    </Box>
  );
};
